import React, { FC, useCallback, useEffect, useState } from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay/hooks';
import { waitForProductExists_checkProductExistsQuery } from './__generated__/waitForProductExists_checkProductExistsQuery.graphql';

const useWaitUntilProductExists = () => {
    const environment = useRelayEnvironment();
    return useCallback(async (productId: string) => {
        // Wait until the product is saved
        while (true) {
            await new Promise((resolve) => setTimeout(resolve, 500));
            const existsResponse = await fetchQuery<waitForProductExists_checkProductExistsQuery>(
                environment,
                graphql`
                    query waitForProductExists_checkProductExistsQuery($productId: String!) {
                        existsProductOnDesignService(productId: $productId)
                        existsProductOnInterfaceService(productId: $productId)
                    }
                `,
                {
                    productId
                }
            ).toPromise();
            if (existsResponse?.existsProductOnDesignService && existsResponse?.existsProductOnInterfaceService) {
                return;
            }
        }
    }, []);
};

const useProductReady = (productId: string) => {
    const [ready, setReady] = useState(false);
    const waitUntilProductExists = useWaitUntilProductExists();
    useEffect(() => {
        waitUntilProductExists(productId).then(() => setReady(true));
    }, [productId, waitUntilProductExists]);
    return ready;
};

type WaitForProductExistsProps = {
    productId: string;
    fallback?: React.ReactNode;
    children: React.ReactNode;
};
const WaitForProductExists: FC<WaitForProductExistsProps> = ({ productId, fallback, children }) => {
    const ready = useProductReady(productId);
    if (ready) {
        return <>{children}</>;
    }
    return <>{fallback}</>;
};

export default WaitForProductExists;
